import React from 'react';
import { Link } from 'react-router-dom';
import { LOGO_URL, HOSTEL_INFO, HERO_IMAGE, WHATSAPP_LINK } from '../data/mockData';
import { motion } from 'framer-motion';
import { MapPin, ArrowRight, Bed, UtensilsCrossed, Camera, MapPinIcon, Mountain, Leaf, Flame, Coffee } from 'lucide-react';
import Layout from '../components/Layout';

const iconMap = { mountain: Mountain, leaf: Leaf, flame: Flame, coffee: Coffee };

const exploreTabs = [
  { href: '/rooms', icon: Bed, title: 'Rooms', desc: 'Dorms, private rooms & our family hybrid room.' },
  { href: '/cafe', icon: UtensilsCrossed, title: 'Cafe', desc: 'All-day meals, chai & bonfire evenings.' },
  { href: '/gallery', icon: Camera, title: 'Gallery', desc: 'Life at MISFiTS through real moments.' },
  { href: '/location', icon: MapPinIcon, title: 'Location', desc: 'How to reach us in Shoja valley.' },
];

const HomePage = () => {
  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative min-h-[calc(100vh-80px)] flex flex-col items-center justify-center overflow-hidden -mt-20 pt-20">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="MISFiTS Hostel" className="w-full h-full object-cover opacity-40" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#111111]/60 via-[#111111]/50 to-[#111111]" />
        </div>

        <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex items-center gap-2 text-[#B0A898] mb-10"
          >
            <MapPin size={14} className="text-[#D4A843]" />
            <span className="text-xs tracking-[0.3em] uppercase">{HOSTEL_INFO.location}</span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.2, ease: 'easeOut' }}
            className="mb-8 relative"
          >
            <div className="absolute inset-0 bg-[#D4A843]/10 rounded-full blur-3xl scale-150" />
            <img
              src="/logo-transparent.png"
              alt={HOSTEL_INFO.name}
              className="w-44 h-44 md:w-56 md:h-56 object-contain drop-shadow-2xl relative z-10"
              loading="eager"
            />
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-heading text-4xl md:text-6xl lg:text-7xl font-bold text-[#F5F0E8] tracking-tight leading-[1.1]"
          >
            Where the <span className="italic text-[#D4A843]">MISFiTS</span> find home.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="text-[#8A8070] text-base md:text-lg mt-6 max-w-xl leading-relaxed"
          >
            {HOSTEL_INFO.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="flex flex-col sm:flex-row items-center gap-4 mt-10"
          >
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-[#D4A843] text-[#1C1C1C] px-8 py-3.5 rounded-full text-sm font-semibold hover:bg-[#E8B940] transition-colors duration-300"
            >
              Book on WhatsApp
            </a>
            <Link
              to="/rooms"
              className="flex items-center gap-2 border border-[#3A3A3A] text-[#F5F0E8] px-8 py-3.5 rounded-full text-sm font-medium hover:border-[#D4A843] hover:text-[#D4A843] transition-colors duration-300"
            >
              Explore Rooms &darr;
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.5, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
            className="w-[1px] h-8 bg-gradient-to-b from-[#D4A843]/60 to-transparent"
          />
        </motion.div>
      </section>

      {/* Our Story */}
      <section className="py-20 md:py-28 px-6 lg:px-8 bg-[#111111]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="text-[#D4A843] text-sm uppercase tracking-[0.4em] mb-6">Our Story</motion.p>
          <motion.h2 initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-[#F5F0E8] leading-tight mb-8">
            Built for <span className="italic text-[#D4A843]">wanderers.</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="text-[#7A7060] text-base md:text-lg max-w-2xl mx-auto leading-relaxed mb-12">
            {HOSTEL_INFO.storyText}
          </motion.p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-20">
            {HOSTEL_INFO.highlights.map((item) => {
              const IconComp = iconMap[item.icon];
              return (
                <div key={item.label} className="flex flex-col items-center gap-2 bg-[#1A1A1A] rounded-xl py-5 px-4 border border-[#222222]">
                  <IconComp size={20} className="text-[#D4A843]" />
                  <span className="text-[#B0A898] text-sm font-medium">{item.label}</span>
                </div>
              );
            })}
          </div>

          {/* Explore Tabs */}
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-[#F5F0E8] mb-10">
            Explore <span className="italic text-[#D4A843]">MISFiTS.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {exploreTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.title}
                  to={tab.href}
                  className="group bg-[#1A1A1A] rounded-xl p-6 border border-[#222222] hover:border-[#D4A843]/40 transition-all duration-500 text-left"
                >
                  <div className="w-10 h-10 rounded-lg bg-[#D4A843]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4A843]/20 transition-colors duration-300">
                    <Icon size={20} className="text-[#D4A843]" />
                  </div>
                  <h3 className="text-[#F5F0E8] font-heading font-semibold text-base mb-1.5 group-hover:text-[#D4A843] transition-colors duration-300 flex items-center gap-2">
                    {tab.title}
                    <ArrowRight size={14} className="opacity-0 group-hover:opacity-100 transition-all duration-300" />
                  </h3>
                  <p className="text-[#6A6050] text-xs leading-relaxed">{tab.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
